import axios from "axios";
import { ConversationMessage } from "@/composables/conversation";

/**
 * Fetch paginated messages for a conversation via backend REST API.
 *
 * @param {Object} conversation - The conversation object
 * @param {Object} options - Pagination options
 * @param {number} [options.limit=15] - Number of messages to fetch
 * @param {string} [options.startAfter] - Cursor "<ISO createdAt>|<doc id>" from a previous page's nextCursor
 * @param {string} [options.archivedSid] - Read an ARCHIVED history (sid carried by a merge message) instead of the live one
 * @returns {Promise<{ items: ConversationMessage[], hasMore: boolean, nextCursor: string|null }>}
 */
export async function fetchMessages(conversation, { limit = 15, startAfter, archivedSid } = {}) {
	if (!conversation?.sid) {
		return Promise.resolve({ items: [], hasMore: false, nextCursor: null });
	}

	const params = { limit, order: "desc" };
	
	if (startAfter) {
		params.startAfter = startAfter;
	}

	if (archivedSid) {
		params.archivedSid = archivedSid;
	}

	// use the internal conversationSid for API calls
	const { data } = await axios.get(`conversations/v2/sid/${conversation.sid}/messages`, { params });

	return {
		items: (data.results ?? [])
			.map(msg => ConversationMessage.fromApi(msg))
			.reverse(),
		hasMore: data.hasMore ?? false,
		nextCursor: data.nextCursor ?? null
	};
}

/**
 * Locate the page containing a conversation within a state's display order.
 * Returns its index and inclusive cursor; the snapshot loads the page contents.
 *
 * @param {("active"|"inactive")} state - Conversation state
 * @param {string} sid - Conversation SID to locate
 * @param {Object} options
 * @param {number} [options.limit=20] - Page size
 * @returns {Promise<{ pageIndex: number, startCursor: { id: string, pinned: boolean, updatedAt: string }|null }>}
 */
export async function fetchConversationPageOf(state, sid, { limit = 20 } = {}) {
	const { data } = await axios.get(
		`conversations/v2/state/${state}/page-of/${sid}`,
		{ params: { limit } }
	);

	return {
		pageIndex: data?.pageIndex ?? 0,
		startCursor: data?.startCursor ?? null
	};
}

/**
 * Locate the last page of a state's display order without returning its contents.
 *
 * @param {("active"|"inactive")} state - Conversation state
 * @param {Object} options
 * @param {number} [options.limit=20] - Page size
 * @returns {Promise<{ pageIndex: number, startCursor: { id: string, pinned: boolean, updatedAt: string }|null }>}
 */
export async function fetchConversationsLastPage(state, { limit = 20 } = {}) {
	const { data } = await axios.get(
		`conversations/v2/state/${state}/last-page`,
		{ params: { limit } }
	);

	return {
		pageIndex: data?.pageIndex ?? 0,
		startCursor: data?.startCursor ?? null
	};
}

/**
 * Resolve the conversations matching a list of phone numbers within the
 * current user group. Used by the conversation search.
 *
 * @param {string[]} phones - Phone numbers to resolve
 * @returns {Promise<Array>} Matching conversation entities
 */
export async function searchConversationsByPhones(phones) {
	if (!phones?.length) {
		return [];
	}

	const { data } = await axios.post("conversations/v2/search/by-phones", { phones });

	return data ?? [];
}

/**
 * Fetch a single conversation by SID. Used to restore the last opened
 * conversation when it is not part of the currently loaded page.
 *
 * @param {string} sid - Conversation SID
 * @returns {Promise<Object|null>} Conversation entity or null when not found
 */
export async function fetchConversationBySid(sid) {
	if (!sid) {
		return null;
	}

	try {
		const { data } = await axios.get(`conversations/v2/sid/${sid}`);

		return data ?? null;
	} catch (err) {
		if (err?.response?.status === 404) {
			return null;
		}

		throw err;
	}
}
